// ============================================================================
// Time & Labor — pure surfaces (shell/surface split, TCSD-0097).
// Everything here takes data + callbacks and imports no client, so it can be
// rendered in a node test. The shells (time-console.tsx etc.) own the RPCs.
// ============================================================================

import { Fragment, type ReactNode } from 'react';
import type { ExceptionRow, PunchHistoryRow, RemovedShiftRow, SegmentRow } from './types';
import {
  exceptionStatusLabel,
  geofenceResultChipClass,
  geofenceResultLabel,
  minutesToHours,
  segmentStatusBadgeClass,
  segmentStatusLabel,
  severityBadgeClass,
  severityLabel,
} from './time-vocabulary';
import { formatSiteDateTime, formatSiteTime } from '../../lib/siteTime';

export function SeverityBadge({ severity }: { severity: string }) {
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${severityBadgeClass(severity)}`}>
      {severityLabel(severity)}
    </span>
  );
}

export function SegmentStatusBadge({ status }: { status: string | null }) {
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${segmentStatusBadgeClass(status)}`}>
      {segmentStatusLabel(status)}
    </span>
  );
}

export function GeofenceChip({ result }: { result: string | null | undefined }) {
  return (
    <span className={`inline-flex items-center rounded-lg px-2.5 py-1 text-xs font-medium ${geofenceResultChipClass(result)}`}>
      {geofenceResultLabel(result)}
    </span>
  );
}

/**
 * ⛔ NOT FOR PUNCH TIMES. This reads the VIEWER's clock, so it is only for the
 * few times on these screens that belong to nobody's shift — when a link
 * expires, when a phone was first seen. Every punch, shift and flag time is on
 * the worker's clock through `siteTime` (Ben, 2026-09-24, Decision 3).
 */
export function fmtWhen(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso); // timestamptz — carries a zone, safe to Date()
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

export interface QueueRowProps {
  row: ExceptionRow;
  canReview: boolean;
  /** Correct can be wider than review: the national AM corrects, HR reviews.
   *  Defaults to `canReview`. */
  canCorrect?: boolean;
  onReview: (row: ExceptionRow) => void;
  onCorrect: (segmentId: string) => void;
}

/** One exception in the queue. Auto-handled rows render calm and read-only —
 *  visible for audit, not asking for work. */
export function QueueRow({ row, canReview, canCorrect = canReview, onReview, onCorrect }: QueueRowProps) {
  const autoHandled = row.status === 'auto_resolved';
  return (
    <div className="flex flex-wrap items-start gap-3 border-t border-tcs-border bg-tcs-raised px-4 py-3">
      <div className="min-w-[260px] flex-[2]">
        <div className="text-[10.5px] font-bold uppercase tracking-wider text-tcs-text-muted">
          {row.rule_label}{row.spec_ref ? ` · ${row.spec_ref}` : ''}
        </div>
        <div className="font-semibold text-tcs-text">
          {row.worker_name}
          <span className="ml-2 font-normal text-tcs-text-secondary">{row.place_display}</span>
        </div>
        {row.detail && <div className="mt-0.5 text-xs text-tcs-text-secondary">{row.detail}</div>}
      </div>
      <div className="min-w-[150px] flex-1 text-xs text-tcs-text-muted">
        <div>{formatSiteDateTime(row.created_at, row.site_tz)}</div>
        {row.started_at && (
          <div className="mt-0.5">
            Shift {formatSiteDateTime(row.started_at, row.site_tz)} → {row.ended_at ? formatSiteDateTime(row.ended_at, row.site_tz) : 'open'}
            {row.raw_minutes != null && ` · ${minutesToHours(row.raw_minutes)} h`}
          </div>
        )}
        {row.status !== 'open' && (
          <div className="mt-0.5">
            {exceptionStatusLabel(row.status)}
            {row.review_note ? ` — ${row.review_note}` : ''}
          </div>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <SeverityBadge severity={row.severity} />
        {autoHandled ? (
          <span className="rounded-full border border-st-ok-border bg-st-ok-bg px-2.5 py-0.5 text-[11px] font-semibold text-st-ok-fg">
            auto-handled
          </span>
        ) : row.status === 'open' && (canReview || canCorrect) ? (
          <>
            {row.segment_id && canCorrect && (
              <button
                type="button"
                onClick={() => onCorrect(row.segment_id as string)}
                className="rounded-lg border border-tcs-border-strong bg-tcs-bg px-3 py-1.5 text-xs font-semibold text-tcs-text hover:bg-tcs-surface"
              >
                Correct…
              </button>
            )}
            {canReview && (
              <button
                type="button"
                onClick={() => onReview(row)}
                className="rounded-lg bg-tcs-inverse px-3 py-1.5 text-xs font-semibold text-tcs-inverse-ink hover:bg-tcs-inverse-hover"
              >
                Mark reviewed
              </button>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
}

export interface QueueGroupProps {
  title: string;
  severity: 'high' | 'medium' | 'low';
  children: ReactNode;
  count: number;
}

export function QueueGroup({ title, severity, count, children }: QueueGroupProps) {
  const headClass =
    severity === 'high'
      ? 'bg-st-danger-bg text-st-danger-fg'
      : severity === 'medium'
        ? 'bg-st-warn-bg text-st-warn-fg'
        : 'bg-tcs-surface text-tcs-text-secondary';
  return (
    <section className="overflow-hidden rounded-xl border border-tcs-border">
      <div className={`flex items-center gap-2 px-4 py-2 text-sm font-bold ${headClass}`}>
        {title}
        <span className="rounded-full bg-tcs-raised px-2 py-0.5 text-xs">{count}</span>
      </div>
      {children}
    </section>
  );
}

/**
 * ⛔ THE RECORD OF EVERY REMOVAL, AND IT IS THE SAME TABLE TWICE.
 *
 * Ben chose both places: behind one line at the bottom of a person's punch
 * card, and on its own tab across everybody. That is one component reading one
 * view with and without a worker filter — the per-person list is the module
 * list filtered, never a second build that can drift from it.
 */
export function RemovedTable({ rows, showWorker = true }: {
  rows: RemovedShiftRow[];
  showWorker?: boolean;
}) {
  return (
    <div className="overflow-x-auto rounded-xl border border-tcs-border">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-tcs-surface text-left text-[11px] uppercase tracking-wide text-tcs-text-muted">
            {showWorker && <th className="px-3 py-2 font-semibold">Worker</th>}
            <th className="px-3 py-2 font-semibold">Shift that was removed (worker&rsquo;s time)</th>
            <th className="px-3 py-2 font-semibold">Place</th>
            <th className="px-3 py-2 text-right font-semibold">Hours</th>
            <th className="px-3 py-2 font-semibold">Removed by</th>
            <th className="px-3 py-2 font-semibold">When</th>
            <th className="px-3 py-2 font-semibold">Reason</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.segment_id} className="border-t border-tcs-border bg-tcs-raised">
              {showWorker && <td className="px-3 py-2 font-medium text-tcs-text">{r.worker_name}</td>}
              <td className="px-3 py-2 tabular-nums text-tcs-text">
                {formatSiteDateTime(r.started_at, r.site_tz)}
                {r.ended_at ? ` – ${formatSiteTime(r.ended_at, r.site_tz)}` : ''}
              </td>
              <td className="px-3 py-2 text-tcs-text-secondary">{r.place_display}</td>
              <td className="px-3 py-2 text-right tabular-nums">{r.hours_removed.toFixed(2)}</td>
              {/* Nobody on record means the row predates the removal trail, not
                  that nobody did it — say which, rather than showing a blank. */}
              <td className="px-3 py-2 text-tcs-text-secondary">{r.removed_by_name ?? 'Not recorded'}</td>
              <td className="px-3 py-2 text-tcs-text-secondary">{r.removed_at ? formatSiteDateTime(r.removed_at, r.site_tz) : '—'}</td>
              <td className="px-3 py-2 text-tcs-text-secondary">{r.reason ?? '—'}</td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={showWorker ? 7 : 6} className="px-3 py-8 text-center text-sm text-tcs-text-muted">
                Nothing has been removed.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

/**
 * ⛔ EVERYTHING THAT EVER HAPPENED TO ONE NIGHT'S PUNCHES.
 *
 * Ben, relaying HR (2026-09-22): "There is no way to look at a punch to see
 * the edit history (when/how/where it was punched)."
 *
 * Read it top to bottom and it is a story: what the clock recorded, what
 * somebody changed it to and why, and what finally stands. ⛔ A superseded
 * punch is dimmed and struck, never hidden — the whole reason corrections are
 * written on top rather than over is that the original survives.
 */
export function PunchHistory({ rows, loading, error }: {
  rows: PunchHistoryRow[];
  loading: boolean;
  error: string | null;
}) {
  if (loading) {
    return <div className="px-3 py-4 text-xs text-tcs-text-muted">Reading the history…</div>;
  }
  if (error) {
    return <div className="px-3 py-4 text-xs text-st-danger-fg">{error}</div>;
  }
  if (rows.length === 0) {
    return <div className="px-3 py-4 text-xs text-tcs-text-muted">No punches recorded for this shift.</div>;
  }
  return (
    <div className="rounded-xl border border-tcs-border-strong bg-tcs-overlay p-3">
      <div className="text-[11px] font-bold uppercase tracking-wider text-tcs-text-muted">
        How this shift came to be
      </div>
      <ol className="mt-2 flex flex-col gap-2">
        {rows.map((p) => {
          const replaced = p.superseded_by !== null;
          return (
            <li
              key={p.punch_event_id}
              className={`rounded-lg border px-3 py-2 ${replaced
                ? 'border-tcs-border bg-tcs-surface opacity-60'
                : 'border-tcs-border-strong bg-tcs-raised'}`}
            >
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className={`text-sm font-semibold text-tcs-text ${replaced ? 'line-through' : ''}`}>
                  {p.event_type === 'in' ? 'Clocked in' : p.event_type === 'out' ? 'Clocked out' : p.event_type}
                  {' '}{formatSiteDateTime(p.event_at, p.site_tz)}
                </span>
                <span className="text-[11px] text-tcs-text-muted">{captureWords(p.capture_mode)}</span>
                {replaced && (
                  <span className="rounded-full border border-tcs-border-strong px-2 py-0.5 text-[10px] font-semibold text-tcs-text-muted">
                    replaced
                  </span>
                )}
              </div>
              <div className="mt-0.5 text-[11.5px] text-tcs-text-secondary">
                {p.place_display ?? 'No building named'} · {punchLocationWords(p)}
              </div>
              <div className="mt-0.5 text-[11px] text-tcs-text-muted">
                {/* ⛔ WHEN IT WAS RECORDED, not when it says it happened. On a
                    correction the two are days apart, and that gap is the
                    single most useful fact on this whole panel. */}
                Recorded {formatSiteDateTime(p.created_at, p.site_tz)}
                {p.created_by_name ? ` by ${p.created_by_name}` : ''}
                {p.supersedes_event_id ? ` · replaced ${p.supersedes_event_id}` : ''}
              </div>
              {p.void_reason && (
                <div className="mt-1 text-[11.5px] italic text-tcs-text-secondary">“{p.void_reason}”</div>
              )}
            </li>
          );
        })}
      </ol>
      <div className="mt-2 text-[11px] text-tcs-text-muted">
        Nothing here was ever edited. Every change is a new punch written on top of the one before it.
      </div>
    </div>
  );
}

/** How a punch arrived, in words rather than a database value. */
function captureWords(mode: string | null): string {
  switch (mode) {
    case 'mobile_web': return 'from their own phone';
    case 'kiosk_pin': return 'at the kiosk';
    case 'web_staff': return 'from the office';
    case 'supervisor_manual': return 'typed by hand';
    case 'system_auto_close': return 'closed automatically';
    case 'import': return 'imported';
    case 'worker_attested_correction': return 'the worker answered';
    default: return mode ?? 'unknown';
  }
}

/** Where a punch happened, in words. Never a bare number on its own. */
function punchLocationWords(p: PunchHistoryRow): string {
  if (p.capture_mode === 'supervisor_manual') return 'typed by hand — no location';
  if (p.geofence_result === 'declined') return 'location turned off';
  if (p.geofence_result === 'unavailable') return 'no location available';
  if (p.geofence_result === 'no_fence') return 'no boundary set for this building';
  if (p.distance_from_site_m == null) return 'no location recorded';
  const m = Math.round(p.distance_from_site_m);
  const acc = p.gps_accuracy_m != null ? ` · GPS good to ${Math.round(p.gps_accuracy_m)} m` : '';
  const where = p.geofence_result === 'inside' ? 'on site' : 'away from the building';
  return `${where} · ${m} m from the door${acc}`;
}

export interface SegmentTableProps {
  rows: SegmentRow[];
  /** A yes for every row, or a per-row answer — an account manager corrects
   *  only at their own buildings (rpc_time_correct_segment). */
  canCorrect: boolean | ((row: SegmentRow) => boolean);
  onCorrect: (row: SegmentRow) => void;
  /**
   * ⛔ Removing is its own action, not a checkbox inside Edit (Ben,
   * 2026-09-22). Optional: a caller that only corrects passes nothing and no
   * Remove button appears.
   */
  onRemove?: (row: SegmentRow) => void;
  /** The shift whose history is open, if any — the shell owns the fetch. */
  historyFor?: string | null;
  onToggleHistory?: (segmentId: string | null) => void;
  history?: PunchHistoryRow[];
  historyLoading?: boolean;
  historyError?: string | null;
}

export function SegmentTable({
  rows, canCorrect, onCorrect, onRemove,
  historyFor, onToggleHistory, history = [], historyLoading = false, historyError = null,
}: SegmentTableProps) {
  return (
    <div className="overflow-x-auto rounded-xl border border-tcs-border">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-tcs-surface text-left text-[11px] uppercase tracking-wide text-tcs-text-muted">
            <th className="px-3 py-2 font-semibold">Worker</th>
            <th className="px-3 py-2 font-semibold">Place</th>
            <th className="px-3 py-2 font-semibold">Line</th>
            <th className="px-3 py-2 font-semibold">In (worker&rsquo;s time)</th>
            <th className="px-3 py-2 font-semibold">Out (worker&rsquo;s time)</th>
            <th className="px-3 py-2 text-right font-semibold">Hours</th>
            <th className="px-3 py-2 font-semibold">Status</th>
            <th className="px-3 py-2 font-semibold" />
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => (
            <Fragment key={s.segment_id}>
            <tr className="border-t border-tcs-border bg-tcs-raised">
              <td className="px-3 py-2 font-medium text-tcs-text">
                {s.worker_name}
                {s.open_exceptions > 0 && (
                  <span className="ml-2 rounded-full border border-st-warn-border bg-st-warn-bg px-2 py-0.5 text-[10.5px] font-semibold text-st-warn-fg">
                    {s.open_exceptions} open flag{s.open_exceptions === 1 ? '' : 's'}
                  </span>
                )}
              </td>
              <td className="px-3 py-2 text-tcs-text-secondary">{s.place_display}</td>
              <td className="px-3 py-2 text-tcs-text-secondary">{s.service_line_label ?? '—'}</td>
              <td className="px-3 py-2 tabular-nums">{formatSiteDateTime(s.started_at, s.site_tz)}</td>
              <td className="px-3 py-2 tabular-nums">{s.ended_at ? formatSiteDateTime(s.ended_at, s.site_tz) : '—'}</td>
              <td className="px-3 py-2 text-right tabular-nums">{minutesToHours(s.paid_minutes)}</td>
              <td className="px-3 py-2"><SegmentStatusBadge status={s.segment_status} /></td>
              <td className="px-3 py-2 text-right">
                <div className="flex justify-end gap-1.5">
                  {/* ⛔ HISTORY IS NOT GATED ON BEING ABLE TO CORRECT. Reading
                      what happened to a punch is how somebody decides whether
                      it needs correcting at all, and it writes nothing. */}
                  {onToggleHistory && (
                    <button
                      type="button"
                      onClick={() => onToggleHistory(historyFor === s.segment_id ? null : s.segment_id)}
                      className="rounded-lg border border-tcs-border bg-tcs-bg px-3 py-1.5 text-xs font-semibold text-tcs-text-secondary hover:bg-tcs-surface"
                    >
                      {historyFor === s.segment_id ? 'Hide history' : 'History'}
                    </button>
                  )}
                {(typeof canCorrect === 'function' ? canCorrect(s) : canCorrect) && (
                  <>
                    <button
                      type="button"
                      onClick={() => onCorrect(s)}
                      className="rounded-lg border border-tcs-border-strong bg-tcs-bg px-3 py-1.5 text-xs font-semibold text-tcs-text hover:bg-tcs-surface"
                    >
                      Edit…
                    </button>
                    {onRemove && (
                      <button
                        type="button"
                        onClick={() => onRemove(s)}
                        className="rounded-lg border border-st-danger-border bg-st-danger-bg px-3 py-1.5 text-xs font-semibold text-st-danger-fg hover:bg-st-danger-border"
                      >
                        Remove…
                      </button>
                    )}
                  </>
                )}
                </div>
              </td>
            </tr>
            {historyFor === s.segment_id && (
              <tr className="border-t border-tcs-border bg-tcs-surface">
                <td colSpan={8} className="px-3 pb-3 pt-1">
                  <PunchHistory rows={history} loading={historyLoading} error={historyError} />
                </td>
              </tr>
            )}
            </Fragment>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={8} className="px-3 py-8 text-center text-sm text-tcs-text-muted">
                No shifts yet. Punch one in from the Punch tab.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
